import React, { Fragment, useState } from 'react';
import { usePolling } from '../hooks/useApi';
import { api } from '../utils/api';
import { truncateDecimalAmount } from '../utils/numberFormat';
import { formatUtc3DateTime, timeAgo } from '../utils/time';
import type { RouterHistoryItem, RouterHistorySource } from '../utils/routerHistory';
import './RouterHistory.css';

const labels: Record<string, string> = {
  submitting: 'Отправляется', triggering: 'Отправляется', submitted: 'Отправлено', confirming: 'Подтверждается',
  completed: 'Восстановлено', recovered: 'Восстановлено', filled: 'Исполнено', refunded: 'Возврат',
  partial: 'Частично', intermediate_returned: 'Промежуточный актив', recovering: 'Восстановление',
  attention: 'Нужна сверка', blocked: 'Заблокировано', failed: 'Ошибка', cancelled: 'Отменено',
  retry_wait: 'Ждёт повтора', paused: 'Пауза', active: 'Активен', unknown: 'Неизвестно',
};
const kinds: Record<string, string> = {
  automatic: 'Автовозврат', manual: 'Ручной возврат', recovery: 'Возврат',
  original: 'Лимитная покупка', continue: 'Продолжение лимитки', unwind: 'Возврат лимитки',
};

function badge(status: string) {
  if (['completed', 'recovered', 'filled', 'refunded'].includes(status)) return 'badge-success';
  if (['failed', 'attention'].includes(status)) return 'badge-danger';
  if (['cancelled', 'unknown'].includes(status)) return 'badge-neutral';
  return 'badge-warning';
}

function Amount({ amount, token }: { amount: string | null; token?: string | null }) {
  return <span className="router-history-amount" title={amount === null ? undefined : amount + (token ? ' ' + token : '')}>
    {truncateDecimalAmount(amount, 2)}{amount !== null && token ? ' ' + token : ''}
  </span>;
}

function EntryDetails({ item }: { item: RouterHistoryItem }) {
  return <div className="router-history-details">
    {item.reason && <p>{item.reason}</p>}
    <dl>
      <div><dt>Исходная сумма</dt><dd><Amount amount={item.originalAmount} token={item.originalToken} /></dd></div>
      <div><dt>Всего восстановлено</dt><dd><Amount amount={item.recoveredAmount} token={item.originalToken} /></dd></div>
      <div><dt>TON gas в USDT</dt><dd>{item.gasUsdt === null ? 'Не определён' : <Amount amount={item.gasUsdt} />}</dd></div>
      {item.orderStatus && <div><dt>Лимитка сейчас</dt><dd>{labels[item.orderStatus] || item.orderStatus}</dd></div>}
      {item.updatedAt && <div><dt>Последняя сверка / обновление</dt><dd>{formatUtc3DateTime(item.updatedAt)}</dd></div>}
      {item.completedAt && <div><dt>Завершено</dt><dd>{formatUtc3DateTime(item.completedAt)}</dd></div>}
    </dl>
    {item.timeBasis === 'trade' && <p className="text-muted">Для старого возврата сохранено только время исходной сделки.</p>}
    {item.legs.length === 0 && <p className="text-muted">Отправки возврата не зафиксированы.</p>}
    {item.legs.map((leg, index) => <div className="router-history-leg" key={index}>
      <strong>{leg.tokenIn || '?'} {'→'} {leg.tokenOut || '?'} · {leg.provider || 'Роутер неизвестен'}</strong>
      <dl>
        <div><dt>Сумма попытки</dt><dd><Amount amount={leg.amountIn} token={leg.tokenIn} /></dd></div>
        <div><dt>Ожидали</dt><dd><Amount amount={leg.expectedAmountOut} token={leg.tokenOut} /></dd></div>
        <div><dt>Минимум</dt><dd><Amount amount={leg.minAmountOut} token={leg.tokenOut} /></dd></div>
        <div><dt>Получили</dt><dd><Amount amount={leg.actualAmountOut} token={leg.tokenOut} /></dd></div>
      </dl>
      {leg.hash && <div>Hash сообщения: <code>{leg.hash}</code></div>}
      {leg.traceId && <div>Trace: <code>{leg.traceId}</code></div>}
      {leg.reason && leg.reason !== item.reason && <p>{leg.reason}</p>}
    </div>)}
  </div>;
}

function HistoryPage({ source, offset, onPage }: { source: RouterHistorySource; offset: number; onPage: (offset: number) => void }) {
  const { data, error, loading, refetch } = usePolling(() => api.getRouterHistory(source, offset), 10000);
  const [expanded, setExpanded] = useState<string | null>(null);
  return <>
    {error && <div className="router-history-error" role="alert">
      Не удалось обновить историю: {error}. {data && 'Показаны ранее загруженные данные.'}
      <button className="btn btn-sm" type="button" onClick={() => void refetch()}>Повторить</button>
    </div>}
    {loading && !data && <p className="text-muted" role="status">Загружаем историю...</p>}
    {data && <>
      {data.items.length === 0 ? <p className="text-muted">Записей пока нет.</p> : <div className="router-history-scroll">
        <table className="table router-history-table">
          <thead><tr>
            <th>Когда</th><th>Операция</th><th>Статус</th><th>Сумма попытки</th><th>Получили по факту</th>
            <th>Роутер</th><th>Оценка потери, USDT</th><th><span className="text-muted">Детали</span></th>
          </tr></thead>
          <tbody>{data.items.map(item => <Fragment key={item.key}>
            <tr>
              <td><time dateTime={item.createdAt}>{timeAgo(item.createdAt)}</time>
                <small>{formatUtc3DateTime(item.createdAt)}</small>
                {item.timeBasis === 'trade' && <small>Время сделки</small>}</td>
              <td><strong>{item.token} · {kinds[item.kind] || item.kind}</strong>
                <small>{item.source === 'recovery' ? 'Сделка' : 'Лимитка'} #{item.referenceId}
                  {item.attemptNo !== null && ' · попытка ' + item.attemptNo}</small></td>
              <td><span className={'badge ' + badge(item.status)}>{labels[item.status] || item.status}</span></td>
              <td>{item.legs.length ? item.legs.map((leg, i) => <div key={i}><Amount amount={leg.amountIn} token={leg.tokenIn} /></div>) : '-'}</td>
              <td>{item.received.length ? item.received.map((asset, i) => <div key={i}>
                <Amount amount={asset.amount} token={asset.token} />
                {asset.kind !== 'output' && <small>{asset.kind === 'refund' ? 'Возврат' : 'Промежуточный'}</small>}
              </div>) : '-'}</td>
              <td>{Array.from(new Set(item.legs.map(leg => leg.provider).filter(Boolean))).join(', ') || '-'}</td>
              <td><Amount amount={item.lossUsdt} /></td>
              <td><button type="button" className="btn btn-sm" aria-expanded={expanded === item.key}
                aria-label={'Детали ' + (item.source === 'recovery' ? 'возврата сделки #' : 'лимитки #') + item.referenceId + (item.attemptNo !== null ? ', попытка ' + item.attemptNo : '')}
                onClick={() => setExpanded(expanded === item.key ? null : item.key)}>{expanded === item.key ? 'Скрыть' : 'Подробнее'}</button></td>
            </tr>
            {expanded === item.key && <tr><td colSpan={8}><EntryDetails item={item} /></td></tr>}
          </Fragment>)}</tbody>
        </table>
      </div>}
      <div className="router-history-pagination">
        <span className="text-muted text-sm">{data.total ? `${offset + 1}–${offset + data.items.length} из ${data.total}` : '0 записей'}</span>
        <div>
          <button type="button" className="btn btn-sm" disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - 20))}>Назад</button>
          <button type="button" className="btn btn-sm" disabled={!data.hasMore} onClick={() => onPage(offset + 20)}>Далее</button>
        </div>
      </div>
    </>}
  </>;
}

export function RouterHistory() {
  const [source, setSource] = useState<RouterHistorySource>('all');
  const [offset, setOffset] = useState(0);
  return <section className="router-history" aria-labelledby="router-history-title">
    <div className="router-history-heading">
      <h2 id="router-history-title">История роутера</h2>
      <select aria-label="Тип операции" value={source} onChange={event => { setSource(event.target.value as RouterHistorySource); setOffset(0); }}>
        <option value="all">Все операции</option><option value="recovery">Возвраты сделок</option><option value="limit">Лимитки</option>
      </select>
    </div>
    <p className="text-xs text-muted">Возвраты и попытки лимиток. Оценка потери не гарантирует учёт TON gas; фактические суммы и gas показаны отдельно.</p>
    <HistoryPage key={source + ':' + offset} source={source} offset={offset} onPage={setOffset} />
  </section>;
}
